import React, { useState, useEffect, useRef } from "react";
import accountingReportsApi from "../../api/accountingReportsApi";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import {
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import ReportPrintHeader from "../../components/ReportPrintHeader.jsx";

export default function IncomeStatementReport() {
  const [fromDate, setFromDate] = useState(new Date().getFullYear() + "-01-01");
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const printRef = useRef();

  const handleSearch = async () => {
    setLoading(true);
    try {
      const res = await accountingReportsApi.getIncomeStatement({
        fromDate,
        toDate,
      });
      setReportData(res.data);
    } catch (error) {
      console.error(error);
      toast.error("فشل جلب قائمة الدخل");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `IncomeStatement-${new Date().toISOString()}`,
  });

  const SectionTable = ({ title, data, total, color }) => (
    <Box mb={4}>
      <Typography variant="h6" fontWeight="bold" color={color} gutterBottom>
        {title}
      </Typography>
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f5f5f5" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>رقم الحساب</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>اسم الحساب</TableCell>
              <TableCell align="left" sx={{ fontWeight: "bold" }}>المبلغ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow key={row.accountId} hover>
                <TableCell>{row.accountCode}</TableCell>
                <TableCell>{row.accountName}</TableCell>
                <TableCell align="left">{row.amount.toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {data.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ color: "text.secondary" }}>
                  لا توجد بيانات
                </TableCell>
              </TableRow>
            )}
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              <TableCell colSpan={2} align="right" sx={{ fontWeight: "bold" }}>
                إجمالي {title}
              </TableCell>
              <TableCell align="left" sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                {total.toLocaleString()}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Box p={3} dir="rtl">
      <Typography variant="h4" fontWeight="bold" mb={3} color="primary">
        📉 قائمة الدخل (Income Statement)
      </Typography>

      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: 2 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                label="من تاريخ"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                label="إلى تاريخ"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2} display="flex" gap={1}>
              <Button
                variant="contained"
                size="large"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
                fullWidth
                sx={{ height: 56 }}
              >
                {loading ? "..." : "بحث"}
              </Button>
              {reportData && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handlePrint}
                  sx={{ height: 56, minWidth: 56 }}
                >
                  <PrintIcon />
                </Button>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {reportData && (
        <div ref={printRef} style={{ padding: "20px", backgroundColor: "white" }}>
          
          <Box>
             <ReportPrintHeader 
                title="قائمة الدخل"
                dateRange={`من ${new Date(fromDate).toLocaleDateString("en-GB")} إلى ${new Date(toDate).toLocaleDateString("en-GB")}`}
             />
             <style>{`
                @media print {
                   .no-print { display: none !important; }
                   /* Ensure table header backgrounds print */
                   thead { background-color: #f0f0f0 !important; -webkit-print-color-adjust: exact; }
                   th { background-color: #f0f0f0 !important; -webkit-print-color-adjust: exact; }
                   /* Force colored rows to print */
                   tr.colored-row { -webkit-print-color-adjust: exact; }
                }
             `}</style>
          </Box>

          {/* الإيرادات */}
          <SectionTable 
            title="الإيرادات (Revenues)" 
            data={reportData.revenues} 
            total={reportData.totalRevenue} 
            color="#2e7d32"
          />

          {/* المصروفات */}
          <SectionTable 
            title="المصروفات (Expenses)" 
            data={reportData.expenses} 
            total={reportData.totalExpense} 
            color="#c62828"
          />

          <Divider sx={{ my: 3 }} />

          {/* صافي الربح/الخسارة */}
          <Box 
            p={3} 
            bgcolor={reportData.netIncome >= 0 ? "#e8f5e9" : "#ffebee"} 
            borderRadius={2}
            textAlign="center"
            border={1}
            borderColor={reportData.netIncome >= 0 ? "#a5d6a7" : "#ef9a9a"}
            className="colored-row"
            sx={{ pageBreakInside: "avoid" }}
          >
            <Typography variant="h5" fontWeight="bold" color="text.primary">
              صافي الدخل (Net Income)
            </Typography>
            <Typography 
              variant="h3" 
              fontWeight="bold" 
              color={reportData.netIncome >= 0 ? "#1b5e20" : "#b71c1c"}
              mt={1}
            >
              {reportData.netIncome.toLocaleString()}
            </Typography>
          </Box>
        </div>
      )}
    </Box>
  );
}
